/*
 * aiClerk 积分余额上下文
 *
 * 持有当前用户积分余额（GET /api/credits/balance），多处共享 + 统一刷新：
 *   - Provider mount（进入 aiClerk，登录后）拉一次
 *   - 每次 SSE 会话完成后 refresh()
 *   - 入口卡片 / 余额=0 拦截逻辑读 credits
 *
 * credits 为 null 表示「暂不可用」（计费故障 / 非 platform 部署），调用方别当 0。
 * 刷新策略：
 *   - 拿到正常数据 → 写入
 *   - 拿到三字段全 null / null 对象（接口故障偶现）→ **自动重试一次**（延迟 500ms），
 *     重试仍 invalid 才走兜底：
 *     · 当前已有有效值 → 保留旧值（避免把故障误显示成清零）
 *     · 当前也没有 → 写入 null（让调用方走「暂不可用」分支）
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { fetchCreditsBalance } from '../api'
import type { CreditsBalance } from '../api'

/** 判定接口返回是否「无效」（null 或三关键字段全 null） */
function isInvalidBalance(c: CreditsBalance | null): boolean {
  return (
    !c || (c.available == null && c.consumed == null && c.total_quota == null)
  )
}

/** 重试间隔：故障首次返回后 500ms 再拉一次（够后端从瞬态故障恢复，又不让用户等太久） */
const RETRY_DELAY_MS = 500

interface CreditsCtxValue {
  /** 当前积分余额；null = 暂不可用 / 未拉到 */
  credits: CreditsBalance | null
  /** 刷新余额，返回最新值（同时写入 context） */
  refresh: () => Promise<CreditsBalance | null>
}

const CreditsCtx = createContext<CreditsCtxValue>({
  credits: null,
  refresh: async () => null
})

export const CreditsProvider: React.FC<{ children?: React.ReactNode }> = ({
  children
}) => {
  const [credits, setCredits] = useState<CreditsBalance | null>(null)
  // 持有最新 credits，供 refresh 内判断「当前是否已有有效值」（避免闭包拿到旧值）
  const creditsRef = useRef<CreditsBalance | null>(null)
  creditsRef.current = credits

  const refresh = useCallback(async () => {
    // 第一次拉余额
    let c = await fetchCreditsBalance()
    // 接口偶现服务端错误返回 {available:null, consumed:null, total_quota:null, ...}
    // 或网络故障返回 null。这种情况延迟 500ms 自动重试一次（后端瞬态故障多半已恢复）。
    if (isInvalidBalance(c)) {
      await new Promise<void>((resolve) =>
        window.setTimeout(resolve, RETRY_DELAY_MS)
      )
      c = await fetchCreditsBalance()
    }
    // 重试后仍 invalid：有旧值保留旧值（避免「接口错误」误显示成「余额清零」），
    // 没旧值则写入 null（调用方走「暂不可用」分支）。
    if (isInvalidBalance(c)) {
      const current = creditsRef.current
      if (current && current.available != null) {
        return current
      }
      setCredits(c)
      return c
    }
    setCredits(c)
    return c
  }, [])

  // 进入 aiClerk（登录后）拉一次
  useEffect(() => {
    refresh()
  }, [refresh])

  const value = useMemo<CreditsCtxValue>(
    () => ({ credits, refresh }),
    [credits, refresh]
  )

  return <CreditsCtx.Provider value={value}>{children}</CreditsCtx.Provider>
}

export function useCredits(): CreditsCtxValue {
  return useContext(CreditsCtx)
}
